import { createContext, useContext, type ReactNode } from 'react';

export type PlatformType = 'web' | 'desktop' | 'mobile';

interface PlatformContextValue {
  platform: PlatformType;
  isTauri: boolean;
  isDesktop: boolean;
  isMobile: boolean;
  isWeb: boolean;
}

const PlatformContext = createContext<PlatformContextValue>({
  platform: 'web',
  isTauri: false,
  isDesktop: false,
  isMobile: false,
  isWeb: true,
});

export function usePlatform(): PlatformContextValue {
  return useContext(PlatformContext);
}

function detectPlatform(): PlatformType {
  if (typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window) {
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('android') || userAgent.includes('iphone') || userAgent.includes('ipad')) {
      return 'mobile';
    }
    return 'desktop';
  }
  return 'web';
}

interface PlatformProviderProps {
  children: ReactNode;
  platform?: PlatformType;
}

export function PlatformProvider({ children, platform: overridePlatform }: PlatformProviderProps) {
  const platform = overridePlatform ?? detectPlatform();
  const isTauri = platform === 'desktop' || platform === 'mobile';

  const value: PlatformContextValue = {
    platform,
    isTauri,
    isDesktop: platform === 'desktop',
    isMobile: platform === 'mobile',
    isWeb: platform === 'web',
  };

  return (
    <PlatformContext.Provider value={value}>
      {children}
    </PlatformContext.Provider>
  );
}
